// src/components/DepartmentForm.tsx
import React, { useState, useEffect } from 'react';
import { X, Shield, AlertCircle } from 'lucide-react';
import { Department } from '../types/department';

interface DepartmentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: Department | null;
  isLoading?: boolean;
}

export function DepartmentForm({ isOpen, onClose, onSubmit, initialData, isLoading }: DepartmentFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Check if department is system default
  const isSystemDepartment = initialData?.is_default === true || 
                            initialData?.created_by === 'system';

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSystemDepartment) {
      return; // Prevent submission for system departments
    }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-300">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {initialData ? 'Edit Department' : 'Add New Department'}
            </h3>
            {isSystemDepartment && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                System Default Department
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Warning for system departments */}
          {isSystemDepartment && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">System department protection</p>
                  <p className="text-xs mt-1">System default departments cannot be edited or deleted. This ensures system integrity.</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Department Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSystemDepartment}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                isSystemDepartment ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="e.g., Engineering, Human Resources, Sales"
              autoFocus={!isSystemDepartment}
            />
            {isSystemDepartment && (
              <p className="text-xs text-gray-500 mt-1">
                System departments cannot be renamed
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              disabled={isSystemDepartment}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                isSystemDepartment ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Brief description of the department..."
            />
          </div>

          {/* Department metadata for system departments */}
          {isSystemDepartment && initialData && (
            <div className="pt-2 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                <div>
                  <span className="font-medium">Created:</span>
                  <p>{new Date(initialData.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="font-medium">Created By:</span>
                  <p>{initialData.created_by === 'system' ? 'System' : initialData.created_by?.substring(0, 8) + '...'}</p>
                </div>
                {initialData.updated_at && (
                  <div className="col-span-2">
                    <span className="font-medium">Last Updated:</span>
                    <p>{new Date(initialData.updated_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {!isSystemDepartment && (
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : initialData ? 'Update Department' : 'Create Department'}
              </button>
            )}
            {isSystemDepartment && (
              <button
                type="button"
                disabled={true}
                className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
              >
                Protected
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}